import { domainLabel, domainSafetyNote, headlineAttribute, rankLabel } from "../../lib/manifest";
import { DateStamp } from "../DateStamp";
import { PhotoMount } from "../PhotoMount";
import { RarityBadge } from "../RarityBadge";
import { SafetyStrip } from "../SafetyStrip";
import type { ScrapbookTemplateProps } from "../ScrapbookPage";

/**
 * The catalogue card: the general case, and the fallback for any variant the
 * frontend hasn't been taught about.
 *
 * The photograph, the name, the binomial if there is one, one headline fact,
 * and a safety note if it needs one. Everything else lives in the details
 * disclosure further down the page.
 */
export function CatalogueCard({ entry, manifest, revealing = false, onImageError }: ScrapbookTemplateProps) {
  const domain = domainLabel(manifest, entry.domain);
  const rank = rankLabel(manifest, entry.domain, entry.rank);
  const headline = headlineAttribute(manifest, entry.domain, entry.attributes);

  return (
    <article className="sheet grain relative overflow-hidden">
      {/* Card stock header with the punched hole a card index would have. */}
      <header className="relative flex items-center gap-3 border-b-2 border-rule bg-paper-deep px-4 py-3 sm:px-6">
        <span
          aria-hidden="true"
          className="block size-3 shrink-0 rounded-full border border-rule bg-paper shadow-pressed"
        />
        <p className="field-label text-ink">
          {[domain, rank].filter(Boolean).join(" · ") || "Catalogued find"}
        </p>
        <span className="ms-auto">
          <RarityBadge rarity={entry.rarity} />
        </span>
      </header>

      <div className="px-4 py-6 sm:px-6 sm:py-8">
        <PhotoMount
          seedId={entry.id}
          src={entry.imageUrl ?? entry.thumbUrl}
          alt={entry.displayTitle}
          aspect="aspect-4/3"
          revealing={revealing}
          eager
          {...(onImageError ? { onImageError } : {})}
        />

        <div className={revealing ? "animate-settle" : undefined}>
          <div className="mt-8 flex flex-wrap items-end justify-between gap-4">
            <div className="min-w-0">
              <h1 className="display-3 text-3xl leading-[1.1] text-ink sm:text-[2.5rem]">
                {entry.displayTitle}
              </h1>
              {entry.scientificName ? (
                <p className="mt-1.5 font-prose text-lg italic text-ink-soft">{entry.scientificName}</p>
              ) : null}
              {headline ? (
                <p className="mt-2 font-label text-sm text-ink-soft">
                  {headline.label}: <span className="text-ink">{headline.display}</span>
                </p>
              ) : null}
            </div>
            <DateStamp date={entry.foundAt} seedId={entry.id} size="sm" />
          </div>

          {entry.safetyNote ? (
            <SafetyStrip
              note={entry.safetyNote}
              domainNote={domainSafetyNote(manifest, entry.domain)}
              className="mt-6"
            />
          ) : null}
        </div>
      </div>
    </article>
  );
}
